import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { withRouter } from 'react-router-dom';

import { makeStyles } from '@material-ui/core/styles';

import { addAllList, paramConverter } from 'modules/common';
import { makeHeadData } from 'lib/commonLib';
import { mediaTopStatistics, mediaTopList } from 'lib/api/media';

import BasicTable from 'components/molecules/MbBasicTable';
import NewFilterWrap from 'components/molecules/NewFilterWrap';
import NewCommonFilter from 'components/organisms/NewCommonFilter';
import FilterCollection from 'components/organisms/FilterCollection';
import SnackbarWrap from 'components/organisms/Snackbar/SnackbarWrap';

import TopStatisticsGraph from './TopStatisticsGraph';

const useStyles = makeStyles((theme) => ({
  tableWrap: {
    display: 'inline-block',
    width: '50%',
    verticalAlign: 'top',
    '&:not(:last-child)': {
      width: 'calc(50% - 22px)',
      marginRight: 22,
    },
  },
}));

const TopStatistics = () => {
  const classes = useStyles();
  const { t } = useTranslation();

  const [initialization, setInitialization] = useState(false);
  const initParams = {
    mediaId: 'all',
    advrtsPrdtCode: 'all',
    pltfomTpCode: 'all',
    stdDttm: moment().add(-8, 'day').format('YYYYMMDD'),
  };
  const [params, setParams] = useState(initParams);
  const initList = {
    MEDIA_DAY_TOP: [],
    MEDIA_TOP: [],
  };
  const [list, setList] = useState(initList);
  const [loadingFilter, setLoadingFilter] = useState(true);
  const [mediaList, setMediaList] = useState([]);

  const [loadingTable, setLoadingTable] = useState(true);

  const initSnackbar = {
    open: false,
    variant: 'success',
    message: '',
  };
  const [snackbar, setSnackbar] = useState(initSnackbar);

  /**
   * 테이블 나오는 순서
   */
  const tableOrder = [
    { key: 'MEDIA_DAY_TOP', label: '' },
    { key: 'MEDIA_TOP', label: '' },
  ];
  const tableInfoJson = {
    MEDIA_DAY_TOP: {
      headList: [
        {
          id: 'GUBUN',
          label: '구분',
          orderFlag: true,
          type: 'string',
          format: '@',
        },
        {
          id: 'PAR_EPRS_CNT',
          label: '구좌노출수',
          orderFlag: true,
          type: 'number',
          format: '#,##0',
        },
        {
          id: 'CLICK_CNT',
          label: '클릭수',
          orderFlag: true,
          type: 'number',
          format: '#,##0',
        },
        {
          id: 'CLICK_RATE',
          label: '클릭율',
          orderFlag: true,
          type: 'number',
          format: '0.000%',
        },
        {
          id: 'ADVRTS_AMT',
          label: '지출금액',
          orderFlag: true,
          type: 'number',
          format: '#,##0',
        },
      ],
      sort: 'desc',
      order: 'STATS_DTTM',
    },
    MEDIA_TOP: {
      headList: [
        {
          id: 'GUBUN',
          label: '구분',
          orderFlag: true,
          type: 'string',
        },
        {
          id: 'STATS_DTTM',
          label: '날짜',
          orderFlag: true,
          type: 'string',
        },
        {
          id: 'PAR_EPRS_CNT',
          label: '구좌노출수',
          orderFlag: true,
          type: 'number',
          format: '#,##0',
        },
        {
          id: 'CLICK_CNT',
          label: '클릭수',
          orderFlag: true,
          type: 'number',
          format: '#,##0',
        },
        {
          id: 'CLICK_RATE',
          label: '클릭율',
          orderFlag: true,
          type: 'number',
          format: '0.000%',
        },
        {
          id: 'ADVRTS_AMT',
          label: '지출금액',
          orderFlag: true,
          type: 'number',
          format: '#,##0',
        },
      ],
      sort: 'desc',
      order: 'ADVRTS_AMT',
    },
  };

  const customized = {
    GUBUN: {
      makeBody: (rowObj) => {
        return (
          <>
            {rowObj['GUBUN'] === '날짜지정' ? (
              <p
                style={{
                  fontWeight: 'bold',
                  color: 'red',
                  textAlign: 'center',
                }}
              >
                {rowObj['GUBUN']}
              </p>
            ) : (
              <>{rowObj['GUBUN']}</>
            )}
          </>
        );
      },
    },
  };

  useEffect(() => {
    setLoadingFilter(true);

    mediaTopList({
      ...paramConverter(params),
    })
      .then((response) => {
        const { data } = response.data;
        setMediaList(data);
      })
      .catch((error) => {
        setSnackbar({
          ...snackbar,
          open: true,
          variant: 'error',
          message: error,
        });
      })
      .finally(() => {
        setLoadingFilter(false);
      }); // eslint-disable-next-line
  }, []);

  useEffect(() => {
    setLoadingTable(true);
    mediaTopStatistics({
      params: {
        ...paramConverter(params),
      },
    })
      .then((response) => {
        const { data } = response.data;
        setList({
          MEDIA_DAY_TOP: data.MEDIA_DAY_TOP,
          MEDIA_TOP: data.MEDIA_TOP,
        });
      })
      .catch((error) => {
        setSnackbar({
          ...snackbar,
          open: true,
          variant: 'error',
          message: error,
        });
      })
      .finally(() => {
        setLoadingTable(false);
      }); // eslint-disable-next-line
  }, [params]);

  /**
   * 필터 초기화 후 값을 바꿈
   */
  useEffect(() => {
    if (initialization === true) {
      setInitialization(false);
    } // eslint-disable-next-line
  }, [initialization]);

  // header 및 body 컬럼 커스텀

  /**
   * 필터 초기화
   */
  const handleOnReset = () => {
    setInitialization(true);
    setParams(initParams);
  };

  const getParam = (newParams) => {
    if (JSON.stringify(params) !== JSON.stringify(newParams)) {
      setParams(newParams);
    }
  };

  /**
   * 스낵바 닫는 함수
   * 연속 클릭이 들어왔을 때는 기존 스낵바를 유지
   * @param event
   * @param reason
   */
  const handleSnackBarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar({
      ...snackbar,
      open: false,
      variant: 'success',
      message: '',
    });
  };

  return (
    <>
      <NewFilterWrap loading={loadingFilter} handleOnReset={handleOnReset}>
        <NewCommonFilter
          initialization={initialization}
          params={params}
          getParam={getParam}
          useSingleDate
          singleDateId={'stdDttm'}
          useExternalFilter={false}
        />
        <FilterCollection
          label={t('집중관리 매체')}
          name="mediaId"
          params={params}
          value={params.mediaId}
          type="select"
          list={addAllList(mediaList, 'CORP_NAME', 'MEDIA_ID', 'all')}
          callBack={getParam}
          labelKey="CORP_NAME"
          valueKey="MEDIA_ID"
        />
      </NewFilterWrap>

      <TopStatisticsGraph params={params} />
      {(() => {
        const result = [];

        for (let i = 0; i < tableOrder.length; i++) {
          const { key } = tableOrder[i];
          const tableInfo = tableInfoJson[key];

          result.push(
            <div key={key} className={`${classes.tableWrap}`}>
              <BasicTable
                sort={tableInfo.sort}
                order={tableInfo.order}
                list={list[key]}
                headCells={makeHeadData(tableInfo.headList)}
                loading={loadingTable}
                // error={error}
                className={`${key}`}
                customized={customized}
              />
            </div>
          );
        }
        return result;
      })()}

      <SnackbarWrap
        open={snackbar.open}
        onClose={handleSnackBarClose}
        variant={snackbar.variant}
        message={snackbar.message}
      />
    </>
  );
};

export default withRouter(TopStatistics);
